import {
  Activity, Bell, ClipboardList, Clock, Cpu, DollarSign, ExternalLink, FileText, FolderOpen,
  FolderTree, Gauge, GitBranch, History, Lock, MessageSquare, Monitor, RefreshCw, Shield,
  Waypoints, Zap,
} from "lucide-react";
import { Fragment, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { RuntimeSelector } from "@/components/layout/RuntimeSelector";
import { StatCard } from "@/components/shared/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { useRuntimeContext } from "@/hooks/useRuntimeContext";
import { incompleteNotice, probe, probeOr, unreachableNames } from "@/lib/probe";
import { useRuntimeVersions } from "@/hooks/useRuntimeVersions";
import { usePolling } from "@/hooks/usePolling";
import { VersionSkewBadge } from "@/components/shared/VersionSkewBadge";
import { formatDuration } from "@/lib/chart-helpers";
import type { AuditSummary } from "@/types/audit";

// Lazy-load tab content from existing pages
import { Dashboard } from "./Dashboard";
import { Costs } from "./Costs";
import { AgentActivity } from "./AgentActivity";
import { Performance } from "./Performance";
import { Alerts } from "./Alerts";
import { MemoryBrowser } from "./MemoryBrowser";
import { RuntimeFsBrowser } from "./RuntimeFsBrowser";
import { RuntimeGitBrowser } from "./RuntimeGitBrowser";
import { RuntimeTasklist } from "./RuntimeTasklist";
import { RuntimeBusOps } from "./RuntimeBusOps";
import { AuditExplorer } from "./AuditExplorer";
import { LogViewer } from "./LogViewer";
import { SessionsBrowser } from "./SessionsBrowser";
import { PipelineInspector } from "./PipelineInspector";
import { ProcessMonitor } from "./ProcessMonitor";
import { PlatformSecuritySettings } from "./PlatformSecuritySettings";
import { HarnessesBrowser } from "./HarnessesBrowser";

type Tab = "overview" | "dashboard" | "costs" | "agents" | "perf" | "alerts" | "memory" | "files" | "githistory" | "tasklist" | "bus" | "audit" | "logs" | "sessions" | "pipeline" | "processes" | "harnesses" | "security";

// `group` clusters the tabs so the wrapped bar reads as Core / Telemetry /
// Internals / Settings (a thin separator is drawn at each group boundary).
type TabGroup = "core" | "telemetry" | "internals" | "settings";

const TABS: { id: Tab; label: string; icon: React.ElementType; group: TabGroup }[] = [
  { id: "overview", label: "Overview", icon: Monitor, group: "core" },
  { id: "dashboard", label: "Dashboard", icon: Cpu, group: "core" },
  { id: "files", label: "Workspace", icon: FolderTree, group: "core" },
  // Lexiun Git repo browser — the WORKING TREE lives one tab left in
  // "Workspace"; this one is the case's git HISTORY (commits, diffs,
  // file-at-commit). Deliberately adjacent so the pair reads as
  // "now" vs "over time".
  { id: "githistory", label: "Case History (Git)", icon: History, group: "core" },
  // U4b — the platform-admin view of this user's tasklist ("Pendências").
  // Sits beside Workspace: both reach INTO the selected user's runtime.
  { id: "tasklist", label: "Tasklist", icon: ClipboardList, group: "core" },
  // Agent-bus ops panel (agent-bus plan §7 P5 Step 13) — per-runtime alias
  // directory, queue depths, unattended lanes, history, force-requeue.
  { id: "bus", label: "Agent Bus", icon: Waypoints, group: "core" },
  { id: "costs", label: "Costs", icon: DollarSign, group: "telemetry" },
  { id: "agents", label: "Agents", icon: Activity, group: "telemetry" },
  { id: "perf", label: "Perf", icon: Gauge, group: "telemetry" },
  { id: "alerts", label: "Alerts", icon: Bell, group: "telemetry" },
  { id: "memory", label: "Memory", icon: FolderOpen, group: "internals" },
  { id: "audit", label: "Audit", icon: Shield, group: "internals" },
  { id: "logs", label: "Logs", icon: FileText, group: "internals" },
  { id: "sessions", label: "Sessions", icon: MessageSquare, group: "internals" },
  { id: "pipeline", label: "Pipeline", icon: GitBranch, group: "internals" },
  { id: "processes", label: "Processes", icon: Cpu, group: "internals" },
  { id: "harnesses", label: "Harnesses", icon: Zap, group: "internals" },
  { id: "security", label: "Security", icon: Lock, group: "settings" },
];

// ── Overview tab content ─────────────────────────────────────────

type BackendSample = {
  ts: number;
  cpu: { percent: number };
  memory: { percentUsed: number };
  disk: { percentUsed: number };
  uptime: number;
};

type AgentSummaryData = {
  totalRuns: number;
  successRate: number;
  totalCost: number;
};

function OverviewContent() {
  const { rpc, state } = useGatewayRpc();
  // §22 — running build + skew for the SELECTED runtime. Single source
  // = useRuntimeVersions (consumes the operator batch endpoint; never
  // computes skew). Omitted in local-dev (no target) and when the
  // endpoint isn't live yet.
  const { target } = useRuntimeContext();
  const { byUserId, targetVersion, available: versionsAvailable } = useRuntimeVersions();
  const build = target ? byUserId.get(target.userId) : undefined;

  const fetcher = useCallback(async () => {
    // Settled probes (lib/probe.ts). The old fallbacks FABRICATED readings:
    // a failed metrics.agents became {totalRuns:0, successRate:0, totalCost:0}
    // and a failed metrics.system became CPU 0% / Memory 0% / Uptime 0ms —
    // an unreachable runtime rendered as a healthy idle one. `unreachable`
    // lets the cards show "—" instead of an invented zero.
    const [sysProbe, agentsProbe, alertsProbe, auditProbe] = await Promise.all([
      probe(rpc<{ samples: BackendSample[] }>("metrics.system", { since: Date.now() - 300_000 })),
      probe(rpc<{ summary: AgentSummaryData }>("metrics.agents", { since: Date.now() - 86_400_000 })),
      probe(rpc<{ alerts: { resolvedAt?: number }[] }>("metrics.alerts")),
      probe(rpc<AuditSummary>("audit.summary", { sinceMs: 3_600_000 })),
    ]);
    const unreachable = unreachableNames({
      "system metrics": sysProbe,
      "agent metrics": agentsProbe,
      alerts: alertsProbe,
      audit: auditProbe,
    });
    const sysRes = probeOr(sysProbe, { samples: [] as BackendSample[] });
    const agentsRes = probeOr(agentsProbe, {
      summary: { totalRuns: 0, successRate: 0, totalCost: 0 } as AgentSummaryData,
    });
    const alertsRes = probeOr(alertsProbe, { alerts: [] as { resolvedAt?: number }[] });
    const auditRes = probeOr(auditProbe, null as AuditSummary | null);
    const latest = sysRes.samples.at(-1);
    const activeAlerts = (alertsRes.alerts ?? []).filter((a) => !a.resolvedAt).length;
    return {
      cpu: latest?.cpu.percent ?? 0,
      memPercent: latest?.memory.percentUsed ?? 0,
      diskPercent: latest?.disk.percentUsed ?? 0,
      uptime: latest?.uptime ?? 0,
      agentRuns: agentsRes.summary.totalRuns,
      successRate: agentsRes.summary.successRate,
      totalCost: agentsRes.summary.totalCost,
      activeAlerts,
      auditAuth: auditRes?.auth.total ?? 0,
      auditTools: auditRes?.tools.total ?? 0,
      auditBlocked: (auditRes?.auth.blocked ?? 0) + (auditRes?.tools.blocked ?? 0),
      unreachable,
    };
  }, [rpc]);

  const { data } = usePolling(fetcher, 10_000, { enabled: state === "connected" });

  if (state !== "connected") {
    return <p className="py-12 text-center text-sm text-slate-400">Connecting to runtime...</p>;
  }

  return (
    <div className="space-y-6">
      {/* `?? "--"` never fired here: cpu/mem/uptime are always numbers, so a
          failed metrics.system rendered a confident 0.0% / 0ms. Gate on the
          PROBE, not on nullishness. */}
      {(() => {
        const sysDown = data?.unreachable.includes("system metrics") ?? false;
        const alertsDown = data?.unreachable.includes("alerts") ?? false;
        return (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard title="CPU" value={!data || sysDown ? "—" : `${data.cpu.toFixed(1)}%`} sparkColor="var(--color-healthy)" />
            <StatCard title="Memory" value={!data || sysDown ? "—" : `${data.memPercent.toFixed(1)}%`} sparkColor="var(--color-warning)" />
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Uptime</CardTitle></CardHeader>
              <CardContent className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-indigo-500" />
                <span className="text-2xl font-semibold tabular-nums">{!data || sysDown ? "—" : formatDuration(data.uptime * 1000)}</span>
              </CardContent>
            </Card>
            <StatCard
              title="Active Alerts"
              value={!data || alertsDown ? "—" : String(data.activeAlerts)}
              sparkColor={alertsDown ? "var(--color-warning)" : data?.activeAlerts ? "var(--color-critical)" : "var(--color-healthy)"}
            />
          </div>
        );
      })()}

      {incompleteNotice(data?.unreachable ?? [], 4) && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          data-testid="runtime-overview-incomplete"
          role="status"
        >
          {incompleteNotice(data?.unreachable ?? [], 4)}
        </div>
      )}

      {/* §22 — running build + skew for the selected runtime. Hidden in
          local-dev (no target) and until the operator batch endpoint
          is live (versionsAvailable). */}
      {target && versionsAvailable && (
        <Card data-testid="runtime-build-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <GitBranch className="h-4 w-4" /> Build
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            {build ? (
              <>
                <span>
                  <span className="text-muted-foreground">version </span>
                  <span className="font-mono font-semibold" data-testid="runtime-build-version">
                    {build.running?.version ?? "unknown"}
                  </span>
                </span>
                <span>
                  <span className="text-muted-foreground">built </span>
                  <span className="font-mono">{build.running?.builtAt ?? "unknown"}</span>
                </span>
                <span>
                  <span className="text-muted-foreground">openclaw </span>
                  <span className="font-mono">{build.running?.openclawVersion ?? "unknown"}</span>
                </span>
                <VersionSkewBadge skew={build.skew} targetVersion={targetVersion} />
              </>
            ) : (
              // In the batch but no row for this user yet, or runtime
              // unreachable — never blank: show the unreachable state.
              <VersionSkewBadge skew="unreachable" />
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Activity className="h-4 w-4" /> Agent Activity (24h)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {/* An unreachable probe must read "—", never an invented 0 —
                "0 runs / 0% / $0.00" is indistinguishable from a healthy
                idle runtime. */}
            {data?.unreachable.includes("agent metrics") ? (
              <p className="text-muted-foreground">— could not reach agent metrics</p>
            ) : (
              <>
                <p><span className="font-semibold">{data?.agentRuns ?? 0}</span> runs</p>
                <p><span className="font-semibold">{((data?.successRate ?? 0) * 100).toFixed(0)}%</span> success rate</p>
                <p><span className="font-semibold">${(data?.totalCost ?? 0).toFixed(2)}</span> total cost</p>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground"><Shield className="h-4 w-4" /> Audit Summary (1h)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {/* Same rule as Agent Activity: "0 auth decisions / 0 tool calls"
                off a failed audit.summary reads as a quiet runtime, not as a
                missing reading. */}
            {data?.unreachable.includes("audit") ? (
              <p className="text-muted-foreground">— could not reach the audit summary</p>
            ) : (
              <>
                <p><span className="font-semibold">{data?.auditAuth ?? 0}</span> auth decisions</p>
                <p><span className="font-semibold">{data?.auditTools ?? 0}</span> tool calls</p>
                {(data?.auditBlocked ?? 0) > 0 && <p className="text-red-500"><span className="font-semibold">{data!.auditBlocked}</span> blocked</p>}
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Quick Stats</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            {/* `?? "--"` is dead here for the same reason it was on the CPU
                card above: diskPercent is always a number once `data` exists,
                so a failed metrics.system printed a confident 0.0%. Gate on
                the PROBE. */}
            <p><span className="font-semibold">{!data || data.unreachable.includes("system metrics") ? "—" : `${data.diskPercent.toFixed(1)}%`}</span> disk used</p>
            <p>Alerts: <span className="font-semibold">{!data || data.unreachable.includes("alerts") ? "—" : data.activeAlerts}</span></p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ── Tab content mapping ──────────────────────────────────────────

const TAB_COMPONENTS: Record<Tab, React.ComponentType> = {
  overview: OverviewContent,
  dashboard: Dashboard,
  costs: Costs,
  agents: AgentActivity,
  perf: Performance,
  alerts: Alerts,
  memory: MemoryBrowser,
  files: RuntimeFsBrowser,
  githistory: RuntimeGitBrowser,
  tasklist: RuntimeTasklist,
  bus: RuntimeBusOps,
  audit: AuditExplorer,
  logs: LogViewer,
  sessions: SessionsBrowser,
  pipeline: PipelineInspector,
  processes: ProcessMonitor,
  harnesses: HarnessesBrowser,
  security: PlatformSecuritySettings,
};

// ── Main page ────────────────────────────────────────────────────

export function RuntimeOverview() {
  const { target, isLocalDev } = useRuntimeContext();
  const { rpc, state } = useGatewayRpc();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  const handleRefreshConfig = useCallback(async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      const res = await rpc<{ ok: boolean; message: string }>("org.config.refresh");
      setRefreshMsg(res.ok ? "Config synced from lexserver" : res.message ?? "Refresh failed");
    } catch (err) {
      setRefreshMsg("Failed to refresh config");
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(null), 4000);
    }
  }, [rpc]);

  const runtimeLabel = isLocalDev ? "dev-user (Local)" : `${target?.userName} (${target?.orgName})`;
  const statusDot = state === "connected" ? "bg-green-500" : state === "connecting" ? "bg-yellow-500 animate-pulse" : "bg-slate-400";

  const ActiveComponent = TAB_COMPONENTS[activeTab];

  return (
    <div className="flex h-full flex-col">
      {/* Header: runtime identity + selector */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">Runtime</h1>
          <div className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-sm dark:border-slate-700">
            <span className={`h-2 w-2 rounded-full ${statusDot}`} />
            <span className="text-muted-foreground">{runtimeLabel}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefreshConfig}
            disabled={refreshing || state !== "connected"}
            title="Sync credentials from lexserver"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Sync Config
          </button>
          {refreshMsg && (
            <span className="text-xs text-slate-500 dark:text-slate-400">{refreshMsg}</span>
          )}
          {/* Phase 4.2 — deep-link to the platform operational-logs viewer
             with just `container_owner_user_id=<userId>` pre-filled. The
             viewer's filter state is URL-driven, so this reuses the same
             single page rather than embedding a second viewer in the
             runtime tabs.
             Hidden in local-dev (no `target`) since the filter wouldn't
             have a meaningful container owner.
             We deliberately do NOT pre-set `service` because both the
             lexiun plugin (`service=lexiun-runtime`) and the harn-service
             sidecar (`service=lexiun-harn-service`) emit from the same
             container under the same `container_owner_user_id`; pinning
             one would hide the other. Operators can add a service filter
             inside the page if they want the narrower slice. */}
          {target && (
            <Link
              to={`/operational-logs?container_owner_user_id=${encodeURIComponent(target.userId)}`}
              title="Open this runtime's operational logs in the platform viewer"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Op Logs
            </Link>
          )}
          <RuntimeSelector />
        </div>
      </div>

      {/* Tab bar — wraps to multiple rows (no horizontal scroll) with a
          thin separator at each group boundary (Core / Telemetry /
          Internals / Settings). */}
      <div className="mb-4 flex flex-wrap items-center gap-x-0.5 gap-y-1 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((tab, i) => {
          const Icon = tab.icon;
          const newGroup = i > 0 && TABS[i - 1].group !== tab.group;
          return (
            <Fragment key={tab.id}>
              {newGroup && (
                <span aria-hidden className="mx-1.5 h-4 w-px self-center bg-slate-200 dark:bg-slate-700" />
              )}
              <button
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-2 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            </Fragment>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        <ActiveComponent />
      </div>
    </div>
  );
}
